import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Container, Row, Col, Alert, Spinner } from "react-bootstrap";
import HeaderTwo from "../../components/HeaderTwo";
import { BreadcrumbBox } from "../../components/common/Breadcrumb";
import BlogSidebar from "./components/BlogSidebar";
import FooterTwo from "../../components/FooterTwo";
import { Styles } from "./styles/blog.js";
import { BLOGS_ENDPOINT } from "../../config/api";

const PLACEHOLDER_IMAGE = "https://via.placeholder.com/800x500?text=Blog";

const BlogGrid = () => {
  const location = useLocation();
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const searchTerm = params.get("search") || "";
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();

    const loadBlogs = async () => {
      setLoading(true);
      setError("");
      try {
        const query = new URLSearchParams({ limit: "100" });
        if (searchTerm.trim()) {
          query.set("search", searchTerm.trim());
        }
        const response = await fetch(`${BLOGS_ENDPOINT}?${query.toString()}`, {
          signal: controller.signal,
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.message || "Unable to load blogs.");
        }

        const payload = await response.json();
        const published = Array.isArray(payload.blogs) ? payload.blogs : [];
        setBlogs(
          published
            .filter((blog) => blog.status === "published")
            .sort((a, b) => {
              const aTime = a.publishedAt
                ? new Date(a.publishedAt).getTime()
                : 0;
              const bTime = b.publishedAt
                ? new Date(b.publishedAt).getTime()
                : 0;
              return bTime - aTime;
            })
        );
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err.message || "Unable to load blogs.");
          setBlogs([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadBlogs();

    return () => controller.abort();
  }, [searchTerm]);

  return (
    <Styles>
      <div className="main-wrapper blog-grid-page">
        <HeaderTwo />
        <BreadcrumbBox title="Blog Grid" />

        <section className="blog-grid-area">
          <Container>
            <Row>
              <Col lg="9" md="8" sm="7">
                {loading && (
                  <div className="text-center my-5">
                    <Spinner animation="border" role="status" />
                  </div>
                )}
                {!loading && error && <Alert variant="danger">{error}</Alert>}
                {!loading && !error && blogs.length === 0 && (
                  <Alert variant="info">
                    {searchTerm
                      ? `No blogs found for "${searchTerm}".`
                      : "No published blogs available yet."}
                  </Alert>
                )}
                {!loading && !error && blogs.length > 0 && searchTerm && (
                  <Alert variant="light" className="mb-4">
                    Showing results for "{searchTerm}".
                  </Alert>
                )}
                {!loading && !error && blogs.length > 0 && (
                  <Row>
                    {blogs.map((blog) => {
                      const authorName =
                        (blog.authorName || "").trim() || "Admin";
                      const initial = authorName.charAt(0).toUpperCase() || "A";
                      const imageSrc = blog.coverImage || PLACEHOLDER_IMAGE;
                      const publishedDate = blog.publishedAt
                        ? new Date(blog.publishedAt).toLocaleDateString()
                        : "";
                      const excerpt = blog.excerpt
                        ? blog.excerpt.length > 160
                          ? `${blog.excerpt.slice(0, 157)}...`
                          : blog.excerpt
                        : "";
                      const slugPath = `/blog-details${
                        blog.slug
                          ? `?slug=${encodeURIComponent(blog.slug)}`
                          : ""
                      }`;
                      return (
                        <Col lg="6" md="12" key={blog._id}>
                          <div className="blog-item">
                            <div className="blog-img">
                              <Link to={process.env.PUBLIC_URL + slugPath}>
                                <img
                                  src={imageSrc}
                                  alt={blog.title}
                                  className="img-fluid"
                                />
                              </Link>
                            </div>
                            <div className="blog-content">
                              <div className="blog-auth_date d-flex">
                                <div className="author-img d-flex">
                                  <div className="author-avatar">
                                    <span>{initial}</span>
                                  </div>
                                  <p>{authorName}</p>
                                </div>
                                {publishedDate && (
                                  <div className="post-date">
                                    <p>
                                      <i className="las la-calendar"></i>{" "}
                                      {publishedDate}
                                    </p>
                                  </div>
                                )}
                              </div>
                              <div className="blog-title">
                                <h6>
                                  <Link to={process.env.PUBLIC_URL + slugPath}>
                                    {blog.title}
                                  </Link>
                                </h6>
                              </div>
                              {excerpt && (
                                <div className="blog-desc">
                                  <p>{excerpt}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                )}
              </Col>
              <Col lg="3" md="4" sm="5">
                <BlogSidebar />
              </Col>
            </Row>
          </Container>
        </section>

        <FooterTwo />
      </div>
    </Styles>
  );
};

export default BlogGrid;
